import React, { useEffect, useState } from "react";
import FA from "../../fa";
import { MUTED } from "../tokens";
import { ControlBar, Select, SegControl } from "../ui";
import {
  GroupYearSoFar,
  groupHasData,
} from "../../teacher/growth/figure";

// Learning → "Trajectory": the district's or a school's standards accumulating
// over the window, in the same figure a teacher gets for their class.
//
// One of Learning's two lenses, and the one with a time axis. It replaced the
// old per-school metric-over-time lens rather than sitting beside it: that lens
// plotted a chosen impact metric per school, which is a comparison between
// places, and the Adoption tab now answers that question better and with the
// dosage denominators to go with it. What only this can show is the stock and
// the flow — what has been mastered so far, what arrived this month, what is
// sitting open — and where the group is working relative to its own grade.
//
// Same evidence as the By-curriculum lens next door, and the same evidence as
// the teacher's class view, which is the point: a principal and a teacher
// looking at the same school in the same week should be looking at the same
// chart — the whole figure, with nothing added or taken away for this host.
//
// Self-contained, the way UnitMasteryTab is: it owns its scope controls and its
// own fetch, and hosts the page's lens tabs on its own ControlBar so the lens
// nav sits in the same place in every lens.
//
// No focus-cycle control here. The figure's x axis IS the window, and a cycle
// narrows to three to twelve weeks — which would leave a monthly plot with two
// points and a weekly one with the school year's most flattering stretch drawn
// as though it were the year. The Buckets control covers the honest half of
// that need, and Adoption is where a cycle is the right lens.

// State worth putting in a link. Mirrored the same way the other tabs do it:
// state is the source of truth, the URL is a mirror.
const mirror = (updates) => {
  const qp = new URLSearchParams(window.location.search);
  Object.entries(updates).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") qp.delete(k);
    else qp.set(k, String(v));
  });
  const qs = qp.toString();
  window.history.replaceState(
    null,
    "",
    qs ? `${window.location.pathname}?${qs}` : window.location.pathname
  );
};

// `?school=` is the dashboard's existing "which school" param (the Adoption
// page's teacher breakdown reads the same one). Sharing it is deliberate: it
// means the same thing here, so a link scoped to a school keeps that school
// when the reader moves between pages.
const seedInt = (key) => {
  const n = parseInt(new URLSearchParams(window.location.search).get(key), 10);
  return Number.isNaN(n) ? null : n;
};

const gradeName = (g) => (g === 0 ? "Kindergarten" : `Grade ${g}`);

const GroupGrowthTab = ({
  api,
  org,
  windowSpec,
  orgunits = [],
  canScopeSchool = false,
  tabs,
}) => {
  const [school, setSchool] = useState(() => seedInt("school"));
  const [enrolledGrade, setEnrolledGrade] = useState(() =>
    seedInt("enrolled_grade")
  );
  const [subject, setSubject] = useState("");
  // null = follow the window, which is what makes this a default rather than a
  // decision. A school year four weeks old has ONE monthly bucket, and a
  // one-column trajectory is not a trajectory — so the container's own grain
  // (schoolyear.defaultGrain: weekly under eight weeks, monthly after) seeds it,
  // the same way the fleet strip and the engagement mix are seeded. Held as an
  // override rather than written into state on mount so it re-derives as the
  // year grows past eight weeks, instead of pinning October's reader to weekly
  // buckets in May. The plot has no daily mode, so a ?interval=day window lands
  // on the finer of the two it does have.
  const [grainPick, setGrainPick] = useState(null);
  const [data, setData] = useState({ loading: true });

  const orgId = org && org.id;
  const { start, end, ssParam } = windowSpec;
  const interval =
    grainPick || (windowSpec.interval === "month" ? "month" : "week");

  // A superadmin switching orgs must drop a now-invalid school selection back
  // to the whole district rather than asking for somebody else's school.
  useEffect(() => {
    if (school && !orgunits.some((s) => s.id === school)) setSchool(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, orgunits]);

  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    setData((d) => ({ ...d, loading: true }));
    api
      .fetchClassroomGrowth({
        scope: school ? "orgunit" : "organization",
        id: school || orgId,
        group: 1,
        start,
        end,
        subject: subject || null,
        interval,
        enrolled_grade: enrolledGrade,
        standardset: ssParam,
      })
      .then((r) => {
        if (!cancelled) setData({ ...r, loading: false });
      })
      .catch(() => {
        if (!cancelled) setData({ loading: false, error: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, school, enrolledGrade, subject, interval, start, end, ssParam]);

  const schoolName =
    (orgunits.find((s) => s.id === school) || {}).name || null;
  const population = school
    ? schoolName || "this school"
    : (org && org.name) || "this district";

  // Offered off the unnarrowed population, so the grade just filtered away is
  // still in the list.
  const gradeOptions = data.enrolled_grades || [];

  let body;
  if (data.loading)
    body = (
      <div style={{ color: MUTED, padding: 40 }}>
        <FA color="gray" icon="spinner" spin /> Loading the trajectory…
      </div>
    );
  else if (data.error)
    body = (
      <div style={{ color: MUTED, padding: 40 }}>
        Couldn’t load the year for {population}.
      </div>
    );
  else if (!groupHasData(data))
    body = (
      <div style={{ color: MUTED, padding: 40, fontSize: 13 }}>
        Nobody in {population} worked a standard in this window
        {subject ? " for this subject" : ""}
        {enrolledGrade === null ? "" : " at this grade"}
        {data.roster_size ? ` — ${data.roster_size} on the roster` : ""}. Mastery
        is counted inside the selected window only.
      </div>
    );
  else
    body = (
      <GroupYearSoFar
        data={data}
        population={population}
        subject={subject || null}
        titled={false}
        hint="A teacher sees this same figure for their own class."
      />
    );

  return (
    <div>
      {/* SCOPE, on the page's lens bar: which population, which grade of it,
          which subject — between them the whole fetch key. Bucket grain sits
          here too rather than on the plot's own heading line, because unlike
          the other tabs' display controls it refetches. */}
      <ControlBar tabs={tabs}>
        {canScopeSchool && orgunits.length > 0 && (
          <Select
            inline
            label="School"
            value={school || ""}
            onChange={(v) => {
              const next = v ? parseInt(v, 10) : null;
              setSchool(next);
              // A different population may not have the selected grade in it.
              setEnrolledGrade(null);
              mirror({ school: next, enrolled_grade: null });
            }}
            maxWidth={200}
            options={[
              ["", "Whole district"],
              ...orgunits.map((s) => [s.id, s.name]),
            ]}
          />
        )}
        {gradeOptions.length > 1 && (
          <Select
            inline
            label="Grade"
            value={enrolledGrade === null ? "" : String(enrolledGrade)}
            onChange={(v) => {
              const g = v === "" ? null : parseInt(v, 10);
              setEnrolledGrade(g);
              mirror({ enrolled_grade: g });
            }}
            options={[
              ["", "All grades"],
              ...gradeOptions.map((g) => [String(g), gradeName(g)]),
            ]}
          />
        )}
        <SegControl
          inline
          label="Subject"
          value={subject}
          onChange={setSubject}
          options={[
            ["", "Both"],
            ["MATH", "Math"],
            ["READING", "Reading"],
          ]}
        />
        <SegControl
          inline
          label="Buckets"
          value={interval}
          onChange={setGrainPick}
          options={[
            ["month", "Months"],
            ["week", "Weeks"],
          ]}
        />
      </ControlBar>
      {body}
    </div>
  );
};

export { GroupGrowthTab };
